import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, Button, Badge, HStack } from "@chakra-ui/react";
import { FaMicrochip, FaDollarSign, FaWrench, FaChartBar, FaBatteryFull, FaBook, FaArrowRight, FaDownload } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const NAVY = "#141C49";
const SOLAR_GOLD_HOVER = "#a07e10";

const SolarResources: React.FC = () => {
  const resourceCategories = [
    {
      title: "Solar Technology",
      icon: FaMicrochip,
      description: "Understand panel types, inverters, and system components",
      resources: [
        "Monocrystalline vs Polycrystalline Panels",
        "String Inverters vs Microinverters",
        "Solar Panel Efficiency Ratings",
        "Understanding System Monitoring"
      ],
      link: "/solar/resources/technology"
    },
    {
      title: "Incentives & Rebates",
      icon: FaDollarSign,
      description: "Government programs and financial incentives available",
      resources: [
        "Small-scale Technology Certificates (STCs)",
        "Feed-in Tariff Rates by State",
        "Low-Interest Solar Loans",
        "Tax Deductions for Businesses"
      ],
      link: "/solar/resources/incentives"
    },
    {
      title: "Maintenance Guide",
      icon: FaWrench,
      description: "Keep your system running at peak performance",
      resources: [
        "Cleaning Solar Panels Safely",
        "Annual Inspection Checklist",
        "Inverter Maintenance Tips",
        "Troubleshooting Common Issues"
      ],
      link: "/solar/resources/maintenance"
    },
    {
      title: "System Comparisons",
      icon: FaChartBar,
      description: "Compare different solar systems and configurations",
      resources: [
        "5kW vs 10kW System Comparison",
        "Residential vs Commercial Systems",
        "With Battery vs Without Battery",
        "Top Solar Brands Compared"
      ],
      link: "/solar/resources/comparisons"
    },
    {
      title: "Battery Storage Guide",
      icon: FaBatteryFull,
      description: "Everything you need to know about solar batteries",
      resources: [
        "Battery Types & Technologies",
        "Sizing Your Battery System",
        "Battery Warranty & Lifespan",
        "ROI Calculations for Batteries"
      ],
      link: "/solar/resources/battery-guide"
    },
    {
      title: "Educational Content",
      icon: FaBook,
      description: "Guides, videos, and learning materials",
      resources: [
        "How Solar Panels Work",
        "Reading Your Electricity Bill",
        "Understanding Grid Connection",
        "Solar Energy Basics eBook"
      ],
      link: "/solar/resources"
    }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/resources"
        fallbackTitle="Solar Resources"
        fallbackDescription="Educational resources and guides for solar energy systems"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Badge colorScheme="yellow" fontSize="md" px={4} py={2} borderRadius="full">
              KNOWLEDGE CENTER
            </Badge>
            <Heading size="2xl" fontWeight="bold">
              Solar Energy Resources
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="3xl">
              Everything you need to know about solar power - from technology basics to financial incentives
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Resource Categories */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center">Browse by Category</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
            Comprehensive guides and resources to help you make informed decisions about solar energy
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          {resourceCategories.map((category, index) => (
            <Box
              key={index}
              p={8}
              bg="white"
              borderRadius="xl"
              border="2px solid"
              borderColor={SOLAR_GOLD}
              shadow="md"
              _hover={{ shadow: "2xl", transform: "translateY(-4px)" }}
              transition="all 0.3s"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <VStack align="stretch" spacing={4}>
                <HStack spacing={4}>
                  <Icon as={category.icon} boxSize={10} color={SOLAR_GOLD} />
                  <Heading size="md">{category.title}</Heading>
                </HStack>

                <Text color="gray.700" fontSize="sm">
                  {category.description}
                </Text>

                <VStack align="stretch" spacing={2} pt={2}>
                  {category.resources.map((resource, rIndex) => (
                    <HStack key={rIndex} spacing={2}>
                      <Box w={2} h={2} borderRadius="full" bg={SOLAR_GOLD} />
                      <Text fontSize="sm" color="gray.600">
                        {resource}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>

              <Button
                mt={6}
                bg={NAVY}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<FaArrowRight />}
                size="sm"
              >
                Explore Resources
              </Button>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Featured Downloads */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8} mb={12}>
            <Heading size="xl" textAlign="center">Featured Downloads</Heading>
            <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
              Free guides and resources to download
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box
              p={8}
              bg="white"
              borderRadius="xl"
              border="2px solid"
              borderColor={SOLAR_GOLD}
              shadow="md"
            >
              <HStack spacing={4} mb={4}>
                <Icon as={FaBook} boxSize={8} color={SOLAR_GOLD} />
                <VStack align="flex-start" spacing={0}>
                  <Heading size="md">Complete Solar Buyer's Guide</Heading>
                  <Text fontSize="sm" color="gray.600">PDF - 24 pages</Text>
                </VStack>
              </HStack>
              <Text color="gray.700" mb={4}>
                Everything you need to know before purchasing a solar system, including system sizing,
                panel selection, and financing options.
              </Text>
              <Button bg={NAVY} color="white" _hover={{ bg: SOLAR_GOLD_HOVER }} leftIcon={<FaDownload />} size="sm">
                Download PDF
              </Button>
            </Box>

            <Box
              p={8}
              bg="white"
              borderRadius="xl"
              border="2px solid"
              borderColor={SOLAR_GOLD}
              shadow="md"
            >
              <HStack spacing={4} mb={4}>
                <Icon as={FaDollarSign} boxSize={8} color={SOLAR_GOLD} />
                <VStack align="flex-start" spacing={0}>
                  <Heading size="md">2024 Incentives Calculator</Heading>
                  <Text fontSize="sm" color="gray.600">Excel Spreadsheet</Text>
                </VStack>
              </HStack>
              <Text color="gray.700" mb={4}>
                Calculate your STCs, feed-in tariff earnings, and total savings with our interactive
                spreadsheet tool customized for Australian states.
              </Text>
              <Button bg={NAVY} color="white" _hover={{ bg: SOLAR_GOLD_HOVER }} leftIcon={<FaDownload />} size="sm">
                Download Spreadsheet
              </Button>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Quick Stats */}
      <Box bg={brandConfig.colors.primary} color="white" py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} textAlign="center">
            <VStack>
              <Text fontSize="4xl" fontWeight="bold">50+</Text>
              <Text fontSize="sm" opacity={0.9}>Articles & Guides</Text>
            </VStack>
            <VStack>
              <Text fontSize="4xl" fontWeight="bold">20+</Text>
              <Text fontSize="sm" opacity={0.9}>Video Tutorials</Text>
            </VStack>
            <VStack>
              <Text fontSize="4xl" fontWeight="bold">15+</Text>
              <Text fontSize="sm" opacity={0.9}>Free Downloads</Text>
            </VStack>
            <VStack>
              <Text fontSize="4xl" fontWeight="bold">24/7</Text>
              <Text fontSize="sm" opacity={0.9}>Access Anytime</Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxW="container.xl" py={16}>
        <Box
          p={10}
          bg="gray.50"
          borderRadius="2xl"
          border="2px solid"
          borderColor={SOLAR_GOLD}
          textAlign="center"
        >
          <Heading size="xl" mb={4} color={NAVY}>Still Have Questions?</Heading>
          <Text fontSize="lg" color="gray.700" mb={6} maxW="2xl" mx="auto">
            Our solar experts are here to help you understand everything about solar energy and find
            the perfect solution for your needs.
          </Text>
          <HStack spacing={4} justify="center">
            <Button bg={NAVY} color="white" _hover={{ bg: SOLAR_GOLD_HOVER }} size="lg">
              Contact an Expert
            </Button>
            <Button variant="outline" borderColor={NAVY} color={NAVY} _hover={{ bg: "gray.100" }} size="lg">
              View FAQ
            </Button>
          </HStack>
        </Box>
      </Container>

      <SolarFooter />
    </Box>
  );
};

export default SolarResources;
